/**
 * Basemap panel — spec §F5.10 group 1: "basemap (panel with 4 thumb rows, active tinted)".
 *
 * Markup and geometry are the mockup's `#basemapPanel` / `.bm-opt` / `.bm-thumb`. The rows
 * are `/meta/config.basemaps` — **four or five**, depending on whether the deployment holds
 * a CARTO URL or key and can therefore offer the dark variant (`basemaps.ts`, and
 * `apps/meta/basemaps.py` for why the server decides). `MapView` reads the same list and
 * swaps the tile layer when `basemap` changes.
 *
 * Rows are a radio group rather than the mockup's `<div>`s — one basemap is on at a time,
 * and ↑/↓ has to move between them (§I5).
 */
import { useTranslation } from 'react-i18next';

import { useAppStore, type Basemap } from '../../lib/state';
import { resolveBasemap, useBasemaps } from './basemaps';

export interface BasemapPanelProps {
  /** Close after a pick, and on Esc — the panel is a popover off a toggle button. */
  onClose: () => void;
}

export function BasemapPanel({ onClose }: BasemapPanelProps) {
  const { t } = useTranslation();
  const basemap = useAppStore((s) => s.basemap);
  const setBasemap = useAppStore((s) => s.setBasemap);
  const list = useBasemaps();
  // The *rendered* selection, which is not always the stored one: a `bm=dark` link opened on
  // a deployment that serves no dark row leaves `basemap` holding a key with no row, and the
  // panel then tints the row the map is actually showing (§F1's default) rather than none.
  const active = resolveBasemap(list, basemap).key;

  const pick = (key: Basemap) => {
    setBasemap(key);
    onClose();
  };

  return (
    <div
      id="basemapPanel"
      className="show"
      role="radiogroup"
      aria-label={t('tools.basemapPanel')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="sb-title">{t('tools.basemap')}</div>
      {list.map((entry) => (
        <button
          key={entry.key}
          type="button"
          role="radio"
          aria-checked={entry.key === active}
          className={`bm-opt${entry.key === active ? ' active' : ''}`}
          data-bm={entry.key}
          onClick={() => pick(entry.key)}
        >
          <span className="bm-thumb" style={{ background: entry.thumb }} />
          {entry.name}
        </button>
      ))}
    </div>
  );
}

export default BasemapPanel;
